import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';
import { Plus } from 'lucide-react';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import MediaManagerTemplateListElement from 'modules/manager/ui/components/media-manager-template-list-element';
import { PRESET_GRID_CLASS } from 'modules/manager/ui/components/preset-card-metrics';
import { PresetGridSkeleton } from 'modules/manager/ui/components/skeletons';
import React from 'react';
import { AsyncState } from 'shared/utils/async-state';
import { UDButton, UDText } from 'ud-ui/components';

interface IProps {
  templates: MediaManagerTemplate[];
  /** Состояние выдачи, посчитанное `deriveAsyncState` (`.claude/docs/async-loading-states.md`). */
  state: AsyncState;
  className?: string;
  /** Row to flash after it was just created (e.g. duplicated from a preset). */
  highlightedTemplateId?: number | null;

  onCreate: () => void;
  onRetry: () => void;
  onSelect: (template: MediaManagerTemplate) => void;
  onEdit: (template: MediaManagerTemplate) => void;
  onDelete: (template: MediaManagerTemplate) => void;
  /** Возвращает промис выгрузки: кнопка экспорта заперта до её конца (IMGM-1561). */
  onExport: (template: MediaManagerTemplate) => Promise<unknown>;
  onMove?: (template: MediaManagerTemplate) => void;
  isExporting?: boolean;
}

function MediaManagerTemplateList(props: IProps) {
  const {
    templates,
    state,
    className,
    highlightedTemplateId,
    isExporting,
    onCreate,
    onRetry,
    onSelect,
    onEdit,
    onDelete,
    onExport,
    onMove,
  } = props;
  const nodeClasses = classNames(className, 'flex min-h-0 flex-1 flex-col');

  if (state.status === 'loading') {
    // У шаблона организации четыре иконки (с «Переместить») или три — набор тот же, что у карточки.
    return (
      <div className={nodeClasses}>
        <PresetGridSkeleton isAdmin iconsCount={onMove ? 4 : 3} />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className={classNames(nodeClasses, 'items-center justify-center gap-2')} role="alert">
        <UDText variant={'body-2'}>
          <Trans>Не удалось загрузить шаблоны</Trans>
        </UDText>
        <UDButton variant="secondary" onClick={onRetry}>
          <Trans>Повторить</Trans>
        </UDButton>
      </div>
    );
  }

  if (state.status === 'empty') {
    return (
      <div className={classNames(nodeClasses, 'items-center justify-center gap-2')}>
        <UDText variant={'body-2'} className="text-gray-500">
          <Trans>В группе пока нет шаблонов</Trans>
        </UDText>
        <UDButton variant="primary" onClick={onCreate}>
          <Plus size={16} className={'mr-2'} /> <Trans>Добавить шаблон</Trans>
        </UDButton>
      </div>
    );
  }

  return (
    <div className={nodeClasses}>
      {/* Обновление после создания / удаления не прячет список: он приглушается, как у `UDAsyncContent`. */}
      <div
        className={classNames(PRESET_GRID_CLASS, 'transition-opacity', { 'pointer-events-none opacity-50': state.isRefreshing })}
        aria-busy={state.isRefreshing}
        data-testid="template-list-rows"
      >
        {templates.map((template) => (
          <MediaManagerTemplateListElement
            key={template.id}
            template={template}
            isHighlighted={template.id === highlightedTemplateId}
            onClick={onSelect}
            onEdit={onEdit}
            onDelete={onDelete}
            onExport={onExport}
            onMove={onMove}
            isExporting={isExporting}
          />
        ))}
      </div>
    </div>
  );
}

export default MediaManagerTemplateList;
